import * as React from 'react';

import cx from 'classnames';

import warning from '~shared/utils/warning';

import { FocusIndicator } from '../FocusIndicator/FocusIndicator';
import { IconChevron } from '../icons/standard';
import { UseDisclosureProps, useDisclosure } from './useDisclosure';

export interface DisclosureBaseProps {
  children: React.ReactNode;
  expandLabel: string;
  collapseLabel?: string;
}
export type DisclosureProps = DisclosureBaseProps & UseDisclosureProps;

/**
 * Controls visibility of a section of content. Similar to an
 * Accordion pattern, but intended for “standalone” sections.
 *
 * Manages its own state internally by default. Controlling the state
 * can be done by passing in `expanded` and `onToggle` props.
 *
 * Ideally, this would use literal `details` and `summary` elements
 * but those are not supported in IE11.
 *
 * @see https://www.w3.org/TR/wai-aria-practices/#disclosure
 */
export function Disclosure({
  children,
  expandLabel,
  collapseLabel = expandLabel,
  expanded: expandedProp,
  id,
  onToggle,
}: DisclosureProps): React.ReactElement {
  warning(
    typeof expandLabel === 'undefined' || typeof expandLabel === 'string',
    "'expandLabel' must be a string"
  );

  warning(
    typeof collapseLabel === 'undefined' || typeof collapseLabel === 'string',
    "'collapseLabel' must be a string"
  );

  const { expanded, buttonProps, contentProps } = useDisclosure({
    id,
    ...(onToggle ? { onToggle, expanded: expandedProp } : null),
  });

  const buttonClass = cx(
    'group relative px-0 py-2 m-0 transition ease-in-out duration-150',
    'font-display text-display font-500 leading-8 text-blue-900 antialiased',
    'bg-transparent border-0 rounded-2 appearance-none outline-none select-none cursor-pointer'
  );

  return (
    <div>
      <div>
        <button className={buttonClass} {...buttonProps}>
          <div className="flex items-center">
            {expanded ? collapseLabel : expandLabel}
            <div className="flex pl-2 text-grey-800">
              <IconChevron direction={expanded ? 'up' : 'down'} />
            </div>
          </div>
          <FocusIndicator />
        </button>
      </div>
      <div className={expanded ? 'block pt-4' : 'hidden'} {...contentProps}>
        {children}
      </div>
    </div>
  );
}
